import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
} from "../../utils/reporting";

const tooltipClassName =
  "rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg";

const ChartTooltip = ({ active, payload, label, formatter, labelFormatter }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <div className={tooltipClassName}>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {labelFormatter ? labelFormatter(label) : label}
      </div>
      <div className="space-y-1 text-sm">
        {payload.map((entry) => (
          <div
            key={entry.dataKey}
            className="flex items-center justify-between gap-4"
          >
            <span className="inline-flex items-center gap-2 text-slate-600">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              {entry.name}
            </span>
            <span className="font-semibold text-slate-900">
              {formatter
                ? formatter(entry.value, entry.name, entry)
                : formatNumber(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const StatusDonutChart = ({ segments = [], total = 0 }) => {
  const data = segments.map((segment) => ({
    ...segment,
    percent: total > 0 ? (segment.value / total) * 100 : 0,
  }));

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr] lg:items-center">
      <div className="h-[320px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              innerRadius={72}
              outerRadius={112}
              paddingAngle={2}
              strokeWidth={0}
            >
              {data.map((entry) => (
                <Cell key={entry.label} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              content={
                <ChartTooltip
                  formatter={(value) => formatNumber(value)}
                  labelFormatter={() => "Payment Status"}
                />
              }
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="space-y-3">
        <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Total Orders
          </div>
          <div className="mt-1 text-3xl font-semibold text-slate-900">
            {formatNumber(total)}
          </div>
        </div>

        {data.map((segment) => (
          <div
            key={segment.label}
            className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3"
          >
            <div className="flex items-center gap-3">
              <span
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: segment.color }}
              />
              <span className="font-medium text-slate-800">{segment.label}</span>
            </div>
            <div className="text-right text-sm">
              <div className="font-semibold text-slate-900">
                {formatNumber(segment.value)}
              </div>
              <div className="text-slate-500">
                {formatPercent(segment.percent)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const HourlyTrendChart = ({ points = [], currencyCode = "USD" }) => {
  const formatHourOnly = (value) => {
    if (!value) {
      return "-";
    }
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }
    return parsedDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatHourWithDate = (value) => {
    if (!value) {
      return "-";
    }
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }
    return parsedDate.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const data = points.map((point, index) => ({
    ...point,
    xAxisValue: point.hourStart || `slot-${index}`,
    hourLabel: formatHourOnly(point.hourStart),
    ordersCount: Number(point.ordersCount || 0),
    netSales: Number(point.netSales || 0),
  }));

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="h-[320px] rounded-xl border border-slate-100 bg-slate-50 p-4">
          <div className="mb-3 text-sm font-semibold text-slate-700">
            Orders By Hour
          </div>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="xAxisValue"
                stroke="#64748b"
                fontSize={12}
                tickFormatter={formatHourOnly}
              />
              <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} />
              <Tooltip
                content={
                  <ChartTooltip
                    labelFormatter={(label) => `Hour: ${formatHourWithDate(label)}`}
                    formatter={(value) => formatNumber(value)}
                  />
                }
              />
              <Line
                type="monotone"
                dataKey="ordersCount"
                name="Orders"
                stroke="#4f46e5"
                strokeWidth={3}
                dot={{ r: 3, fill: "#4f46e5" }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="h-[320px] rounded-xl border border-slate-100 bg-slate-50 p-4">
          <div className="mb-3 text-sm font-semibold text-slate-700">
            Net Sales By Hour
          </div>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="xAxisValue"
                stroke="#64748b"
                fontSize={12}
                tickFormatter={formatHourOnly}
              />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip
                content={
                  <ChartTooltip
                    labelFormatter={(label) => `Hour: ${formatHourWithDate(label)}`}
                    formatter={(value) => formatCurrency(value, currencyCode)}
                  />
                }
              />
              <Bar
                dataKey="netSales"
                name="Net Sales"
                fill="#10b981"
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-xl border border-slate-100 bg-white p-4">
        <div className="mb-3 text-sm font-semibold text-slate-700">
          Hourly Breakdown
        </div>
        <div className="grid gap-3">
          {data.map((point, index) => (
            <div
              key={`${point.hourStart || point.xAxisValue}-${index}`}
              className="grid gap-2 rounded-lg border border-slate-100 px-4 py-3 md:grid-cols-[140px_1fr_1fr]"
            >
              <div className="font-medium text-slate-800">{point.hourLabel}</div>
              <div className="text-sm text-slate-600">
                Orders: <span className="font-semibold text-slate-900">{formatNumber(point.ordersCount)}</span>
              </div>
              <div className="text-sm text-slate-600">
                Net sales:{" "}
                <span className="font-semibold text-slate-900">
                  {formatCurrency(point.netSales, currencyCode)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const HorizontalBarList = ({
  items = [],
  valueKey = "salesAmount",
  labelKey = "categoryName",
  currencyCode = "USD",
  valueFormatter,
  listVariant = "cards",
}) => {
  const data = items.map((item) => ({
    ...item,
    label: item[labelKey] || "-",
    chartValue: Number(item[valueKey] || 0),
  }));

  return (
    <div className="space-y-4">
      <div className="h-[340px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 10, right: 12, left: 24, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis type="number" stroke="#64748b" fontSize={12} />
            <YAxis
              dataKey="label"
              type="category"
              stroke="#64748b"
              fontSize={12}
              width={120}
            />
            <Tooltip
              content={
                <ChartTooltip
                  labelFormatter={(label) => label}
                  formatter={(value, _name, entry) =>
                    valueFormatter
                      ? valueFormatter(value, entry?.payload)
                      : formatCurrency(value, currencyCode)
                  }
                />
              }
            />
            <Legend />
            <Bar
              dataKey="chartValue"
              name="Sales"
              fill="#4f46e5"
              radius={[0, 6, 6, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {listVariant === "none" ? null : listVariant === "plain" ? (
        <ul className="divide-y divide-slate-100 border-t border-b border-slate-100">
          {data.map((item) => (
            <li
              key={item.categoryId || item.itemId || item.label}
              className="flex items-center justify-between gap-4 py-3"
            >
              <span className="font-medium text-slate-800">{item.label}</span>
              <span className="text-sm text-slate-600">
                {valueFormatter
                  ? valueFormatter(item.chartValue, item)
                  : formatCurrency(item.chartValue, currencyCode)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="grid gap-3">
          {data.map((item) => (
            <div
              key={item.categoryId || item.itemId || item.label}
              className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3"
            >
              <span className="font-medium text-slate-800">{item.label}</span>
              <span className="text-sm text-slate-600">
                {valueFormatter
                  ? valueFormatter(item.chartValue, item)
                  : formatCurrency(item.chartValue, currencyCode)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
